import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, AlertCircle, TrendingUp, Users, BookOpen, Target } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_API_KEY = process.env.REACT_APP_FOUNDER_API_KEY;

// Storage keys
const FOUNDER_CODE_KEY = "founder_access_code";
const CREATOR_TOKEN_KEY = "creator_token";

export default function FounderDashboard() {
  const [founderCode, setFounderCode] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Check for config issues on mount
  useEffect(() => {
    if (!FOUNDER_API_KEY) {
      setConfigError("REACT_APP_FOUNDER_API_KEY is not configured. Contact admin.");
    }
    
    // Load saved founder code
    const savedCode = localStorage.getItem(FOUNDER_CODE_KEY);
    if (savedCode) {
      setFounderCode(savedCode);
    }
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    setMetrics(null);

    // Save the code for next time
    localStorage.setItem(FOUNDER_CODE_KEY, founderCode);

    // Get creator token
    const creatorToken = localStorage.getItem(CREATOR_TOKEN_KEY);
    if (!creatorToken) {
      setError("Please log in as a creator first.");
      setLoading(false);
      return;
    }

    if (!founderCode.trim()) {
      setError("Please enter the founder access code.");
      setLoading(false);
      return;
    }

    if (!FOUNDER_API_KEY) {
      setError("Founder API key not configured.");
      setLoading(false);
      return;
    }

    try {
      console.log("[FounderDashboard] Loading metrics...");
      
      const response = await axios.get(`${API}/founder/secure-metrics`, {
        params: { founder_code: founderCode.trim() },
        headers: {
          "Authorization": `Bearer ${creatorToken}`,
          "X-Founder-Key": FOUNDER_API_KEY
        }
      });

      console.log("[FounderDashboard] Metrics loaded successfully");
      setMetrics(response.data);
    } catch (err) {
      console.error("[FounderDashboard] Error:", err.response?.status, err.response?.data);
      
      if (err.response?.status === 401) {
        setError("Please log in as a creator first.");
      } else if (err.response?.status === 403) {
        setError("Not authorized. Check your access code and account.");
      } else {
        setError("Unable to load metrics. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    loadMetrics();
  };

  // Show config error if FOUNDER_API_KEY is missing
  if (configError) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Configuration Error</h2>
            <p className="text-gray-600">{configError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="w-8 h-8 text-ember" />
            <h1 className="font-heading text-3xl text-sage-900">Founder Metrics</h1>
          </div>
          <p className="text-muted-foreground">Secure access for authorized founders only</p>
        </div>

        {/* Access Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg">Authentication</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="founder-code">Founder Access Code</Label>
                <Input
                  id="founder-code"
                  type="password"
                  value={founderCode}
                  onChange={(e) => setFounderCode(e.target.value)}
                  placeholder="Enter your access code"
                  className="mt-1"
                  data-testid="founder-code-input"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-ember hover:bg-ember/90"
                data-testid="load-metrics-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Load Metrics
                  </>
                )}
              </Button>
            </form>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metrics Display */}
        {metrics && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Challenges Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Challenges</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total</span>
                      <span className="font-semibold">{metrics.metrics.challenges.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last 7 days</span>
                      <span className="font-semibold text-blue-600">{metrics.metrics.challenges.last_7_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last 30 days</span>
                      <span className="font-semibold">{metrics.metrics.challenges.last_30_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Today</span>
                      <span className="font-semibold text-green-600">{metrics.metrics.challenges.today}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Creators Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Creators</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total</span>
                      <span className="font-semibold">{metrics.metrics.creators.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last 7 days</span>
                      <span className="font-semibold text-purple-600">{metrics.metrics.creators.last_7_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last 30 days</span>
                      <span className="font-semibold">{metrics.metrics.creators.last_30_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Today</span>
                      <span className="font-semibold text-green-600">{metrics.metrics.creators.today}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sessions Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Target className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Sessions</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total</span>
                      <span className="font-semibold">{metrics.metrics.sessions.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Completed</span>
                      <span className="font-semibold text-green-600">{metrics.metrics.sessions.completed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last 7 days</span>
                      <span className="font-semibold">{metrics.metrics.sessions.last_7_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Completion Rate</span>
                      <span className="font-semibold text-ember">{metrics.metrics.sessions.completion_rate}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Breakdown Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* By Subject */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">By Subject</CardTitle>
                </CardHeader>
                <CardContent>
                  {metrics.metrics.breakdown.by_subject.length > 0 ? (
                    <div className="space-y-2">
                      {metrics.metrics.breakdown.by_subject.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-1 border-b last:border-0">
                          <span className="text-gray-700">{item.subject}</span>
                          <span className="font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* By Grade Level */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">By Grade Level</CardTitle>
                </CardHeader>
                <CardContent>
                  {metrics.metrics.breakdown.by_grade_level.length > 0 ? (
                    <div className="space-y-2">
                      {metrics.metrics.breakdown.by_grade_level.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-1 border-b last:border-0">
                          <span className="text-gray-700">{item.grade_level}</span>
                          <span className="font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Metadata */}
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-gray-500 flex flex-wrap gap-4">
                  <span>Generated: {new Date(metrics.generated_at).toLocaleString()}</span>
                  <span>User: {metrics.authorized_user}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
